/**
 * Serviço de Autenticação Google Híbrido
 * Gerencia o login Google tanto nativamente (via plugin) quanto na web (via Firebase SDK)
 */

import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { GoogleAuthProvider, signInWithCredential, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebaseConfig';
import { isNativeApp } from '../utils/platformUtils';

let isAuthInProgress = false;

/**
 * Realiza login com Google usando a estratégia adequada para a plataforma
 * @param {Function} [onProgress] Callback para reportar etapa atual ('connecting', 'authenticating', 'success')
 * @returns {Promise<User>} Usuário autenticado
 */
export const signInWithGoogle = async (onProgress) => {
    if (isAuthInProgress) {
        console.warn('[Auth] Login Google já em andamento, ignorando chamada concorrente.');
        return null;
    }

    isAuthInProgress = true;
    try {
        const isNative = isNativeApp();
        console.log(`[Auth] Iniciando login Google. Native? ${isNative} | Platform: ${Capacitor.getPlatform()}`);
        if (onProgress) onProgress('Conectando com o Google...');

        if (isNative) {
            // Android/iOS Nativo: Usa plugin Capacitor Google Auth
            console.log('[Auth] Caminho: Plugin Nativo');

            // Força o seletor de contas limpando qualquer sessão prévia no plugin
            await GoogleAuth.signOut().catch(() => {});

            // O plugin retorna um objeto User que contém o confirmation code ou idToken
            const googleUser = await GoogleAuth.signIn();

            if (onProgress) onProgress('Validando credenciais...');
            console.log('[Auth] Google nativo sucesso, autenticando no Firebase...');

            // Cria credencial do Firebase usando o ID Token retornado pelo Google
            const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);

            // Autentica no Firebase com a credencial
            const result = await signInWithCredential(auth, credential);
            console.log('[Auth] Sucesso no login nativo!');
            if (onProgress) onProgress('Tudo pronto! Entrando...');
            return result.user;
        } else {
            // Web/PWA: Usa popup padrão do Firebase SDK
            console.log('[Auth] Caminho: Firebase Web SDK (Popup)');
            if (onProgress) onProgress('Aguardando autorização...');
            const result = await signInWithPopup(auth, googleProvider);
            console.log('[Auth] Sucesso no login web!');
            if (onProgress) onProgress('Tudo pronto! Entrando...');
            return result.user;
        }
    } catch (error) {
        if (
            error?.message?.includes('cancelled') ||
            error?.message?.includes('closed') ||
            error?.code === 'auth/cancelled-popup-request' ||
            error?.code === 'auth/popup-closed-by-user'
        ) {
            console.log('[Auth] Login cancelado pelo usuário.');
            return null; // Return null to indicate cancellation without crashing
        }
        console.error('[Auth] Erro detalhado no login Google:', error);
        
        // Alerta visual para diagnóstico em tempo real no dispositivo
        if (Capacitor.isNativePlatform()) {
            alert(`Erro no Login Google:\nCode: ${error.code}\nMessage: ${error.message}\nFull: ${JSON.stringify(error)}`);
        }
        
        throw error;
    } finally {
        isAuthInProgress = false;
    }
};

/**
 * Inicializa o plugin de Auth Nativo (necessário chamar na inicialização do app)
 */
export const initializeGoogleAuth = () => {
    if (isNativeApp()) {
        try {
            // WEB Client ID (type 3) - OBRIGATÓRIO como clientId no Android para obter idToken válido para o Firebase
            const CLIENT_ID = '959738717502-eonsj7dqmlrgao9ssj4gsjl18v2ho278.apps.googleusercontent.com';
            
            GoogleAuth.initialize({
                clientId: CLIENT_ID,
                serverClientId: CLIENT_ID,
                scopes: ['profile', 'email'],
                forceCodeForRefreshToken: true,
            });
            console.log('[Auth] GoogleAuth plugin inicializado com Web Client ID');
        } catch (e) {
            console.error('[Auth] Falha ao inicializar GoogleAuth:', e);
        }
    }
};
